import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { DialogProps } from "_shared/_views/dialogs/dialog";
import Guid from "_shared/guids/guid";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { getIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import * as React from "react";
import { useSelector } from "react-redux";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";

/** A team larger than this gets a search box over its list. */
export const TEAM_ROSTER_SEARCH_THRESHOLD = 8;

/**
 * The application's header is drawn above Material's drawer layer, so the paper starts below it, at
 * the header's own heights — as `TemplateDetailsDrawer` does.
 */
const RosterDrawer = styled(Drawer)(({ theme }) => ({
  "& .MuiDrawer-paper": {
    height: `calc(100% - ${theme.spacing(8)} - ${shellTopBannersHeight()})`,
    top: `calc(${theme.spacing(8)} + ${shellTopBannersHeight()})`,
    [theme.breakpoints.down("sm")]: {
      height: `calc(100% - ${theme.spacing(7)} - ${shellTopBannersHeight()})`,
      top: `calc(${theme.spacing(7)} + ${shellTopBannersHeight()})`,
    },
  },
}));

const RosterContent = styled("aside")(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: "100vw",
  padding: theme.spacing(2),
  width: "440px",
}));

const RosterHeader = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "space-between",
}));

const RosterTitle = styled(Typography)(() => ({
  overflowWrap: "anywhere",
}));

const PersonName = styled("button")(({ theme }) => ({
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  overflowWrap: "anywhere",
  padding: 0,
  textAlign: "left",
  textDecoration: "underline",
}));

type TeamRosterDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** The work the team is on, or the template's name. */
  title: string;
  leaderUserId?: Guid;
  memberUserIds: Guid[];
};

/**
 * Everyone on a team, leader first, each with their face, firm and a link to their profile (#6603).
 *
 * A drawer rather than a dialog, so a profile opens over it and closing the profile comes back to the
 * list: a reader can go through the team one person after another.
 */
export default function TeamRosterDrawer(props: Readonly<TeamRosterDrawerProps>) {
  const { open, onClose, title } = props;

  return (
    <RosterDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      // React carries a click inside a portal up to the portal's owner, and the owner is a table row
      // that navigates on click. A click in the list is about the list.
      onClick={(event) => event.stopPropagation()}
    >
      <RosterContent aria-label={`${title} team`}>
        <RosterHeader>
          <RosterTitle variant="h5">{title}</RosterTitle>
          <Tooltip title="Close panel">
            <IconButton aria-label="Close team" onClick={onClose}>
              <KeyboardTabIcon />
            </IconButton>
          </Tooltip>
        </RosterHeader>
        <TeamRoster leaderUserId={props.leaderUserId} memberUserIds={props.memberUserIds} />
      </RosterContent>
    </RosterDrawer>
  );
}

/** How a team member's profile opens, from the Members cell and from the list alike. */
export function individualProfileDialog(individual: Individual): DialogProps {
  return {
    component: <ViewIndividualProfile profile={individual.profile} individualId={individual.id} />,
    contentSxProps: {
      display: "flex",
    },
    MuiProps: {
      fullWidth: true,
      maxWidth: "lg",
    },
    titleStyle: {
      paddingBottom: 0,
    },
  };
}

function TeamRoster(props: Readonly<{ leaderUserId?: Guid; memberUserIds: Guid[] }>) {
  const { leaderUserId, memberUserIds } = props;
  const { openDialog } = useDialog();
  const [search, setSearch] = React.useState("");

  const leaders = useSelector(getIndividualsByUserIds(leaderUserId ? [leaderUserId] : []));
  const members = useSelector(getIndividualsByUserIds(memberUserIds));
  const leader = leaders.length > 0 ? leaders[0] : undefined;
  const people = leader ? [leader, ...members.filter((member) => !member.userId.isEqualTo(leader.userId))] : members;

  const query = search.trim().toLowerCase();
  const shown = query ? people.filter((person) => matches(person, query)) : people;

  return (
    <>
      <Typography color="text.secondary" variant="body2">
        {people.length === 1 ? "1 person" : `${people.length} people`}
      </Typography>
      {people.length > TEAM_ROSTER_SEARCH_THRESHOLD && (
        <TextField
          label="Search team"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      )}
      {shown.length === 0 && (
        <Typography color="text.secondary">
          {query ? "Nobody on this team matches your search." : "Nobody on this team is visible to you."}
        </Typography>
      )}
      <List aria-label="Team members" dense disablePadding>
        {shown.map((person) => (
          <ListItem key={person.userId.value} disableGutters>
            <ListItemAvatar>
              <IndividualAvatar individual={person} />
            </ListItemAvatar>
            <ListItemText
              primary={
                <PersonName
                  type="button"
                  onClick={() => openDialog(individualProfileDialog(person))}
                >
                  {person.fullName}
                </PersonName>
              }
              secondary={person.entityName}
            />
            {person === leader && <Chip label="Leader" size="small" variant="outlined" />}
          </ListItem>
        ))}
      </List>
    </>
  );
}

function matches(person: Individual, query: string): boolean {
  return person.fullName.toLowerCase().includes(query) || (person.entityName ?? "").toLowerCase().includes(query);
}
